/**
 * Carrier MCP — subscriber_country_history tool.
 *
 * Cross-border movement history for a subscriber, reconstructed from the OCS
 * itself.
 *
 * WHAT CHANGED AND WHY
 * --------------------
 * This tool used to answer purely from `country.entered` events in the
 * OCS_EVENT_ROUTING ring buffer, which only ever holds what the Bridge4IP
 * Relay LU webhook pushed into it. Relay is off for reseller 1170
 * (`relayGy: false`, `relayLU: false` on `getResellerInfo.trafficInfo`) and the
 * buffer keeps 24 hours at best, so the tool reported "no cross-border events"
 * for subscribers that had plainly crossed a border.
 *
 * The OCS already knows. `subscriberNetworkEventsOverPeriod` returns
 * `gyAnswer.events` — one Diameter Gy charging record per session
 * Init/Update/Term — and every record carries its own `countryAlpha2`,
 * `countryName`, `operator` and `userLocationInfoMCC`. Gy is now the primary
 * source; the ring buffer is merged in as a supplement when relay happens to be
 * on, so nothing that used to be reported is lost. The Relay LU feed is no
 * longer a dependency: with the buffer empty the answer is still complete for
 * the queried period.
 *
 * Verified live read-only on 2026-09-06 against ICCID 8948010000020241196
 * (reseller 1170, 2026-09-01..2026-09-06): 36 Gy records, HTTP 200,
 * `status.code === 0`, collapsing to NL 09-01 22:36 → BE 09-03 10:19. The
 * shape rules — `usedUnits` is a string that is sometimes "N/A", MCC is a
 * string, `countryAlpha2` is lower-case, and a Term can be stamped after the
 * Init that replaced it — live in `@carrier/ocs-client`.
 *
 * Read-only. Requires "read" scope.
 *
 * TENANCY: the stdio server runs on one operator's own eSIMVault token, so
 * OCS itself is the boundary — getSingleSubscriber ahead of the reads rejects
 * an ICCID outside that account. The ring-buffer key carries no tenant, which
 * is why that call stays where it is even though Gy no longer needs it.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { OcsClient } from "./client.js";
import { wrapHandler, type ToolContext } from "./tools.js";
import {
  gyCountryHistoryFromResponse,
  networkEventsOverPeriodParams,
  type CountryStay,
} from "@carrier/ocs-client";
import {
  listRecentOcsEvents,
  mergeOcsEventOutputsNewestFirst,
  msisdnFieldFromSubscriberPayload,
  ocsEventRingBufferIccidFromDigitsReference,
} from "@carrier/ocs-spec/ocs-event-buffer-read";

/** YYYY-MM-DD, `daysAgo` days before `now`. */
function isoDate(now: Date, daysAgo = 0): string {
  const d = new Date(now.getTime() - daysAgo * 86_400_000);
  return d.toISOString().slice(0, 10);
}

/**
 * Render Gy stays in the flat `country_events` shape this tool has always
 * returned, newest first. The first stay of a period has no predecessor, so
 * its `previous_country` is null rather than invented.
 */
function staysAsCountryEvents(
  stays: CountryStay[],
): Array<Record<string, unknown>> {
  return stays
    .map((stay, idx) => ({
      event_id: `gy-${stay.country}-${stay.first_seen}`,
      new_country: stay.country,
      previous_country: idx === 0 ? null : stays[idx - 1]!.country,
      mcc: stay.mccs[0] ?? null,
      mnc: null,
      timestamp: stay.first_seen,
      source: "ocs-gy",
    }))
    .reverse();
}

export function registerCountryHistoryTool(
  server: McpServer,
  ctx: ToolContext,
): void {
  server.registerTool(
    "subscriber_country_history",
    {
      title: "Subscriber Country History",
      description:
        "Return cross-border movement history for a subscriber, reconstructed from OCS Gy charging events. " +
        "Reads `gyAnswer.events` from `subscriberNetworkEventsOverPeriod` — every Gy record carries its own " +
        "country, operator and MCC — and collapses consecutive same-country records into stays with " +
        "first-seen and last-seen timestamps. This needs no webhook: it works with Relay LU and Relay Gy off. " +
        "Relay `country.entered` events from the 24h ring-buffer are merged in as a supplement when present. " +
        "Use this to answer: 'where has subscriber X traveled', 'show country history for ICCID', " +
        "'border crossings for this SIM'. " +
        "Params: `subscriberId` (ICCID, 19–20 digits), `startDate`/`endDate` (YYYY-MM-DD, max 7 days apart, " +
        "default the last 7 days), `limit` (1–50, default 20, newest first). " +
        "Returns: `stays` ({ country, country_name, first_seen, last_seen, operators, mccs, event_count, " +
        "used_bytes }), `transitions` ({ from, to, at }), and `country_events` in the legacy flat shape. " +
        "`used_bytes` already handles the string-typed `usedUnits` and its 'N/A' values. " +
        "Do NOT use this for raw per-session records — use `subscriber_network_events` for those.",
      inputSchema: {
        subscriberId: z
          .string()
          .regex(/^\d{19,20}$/, "subscriberId must be a 19–20 digit ICCID")
          .describe("Subscriber ICCID (19–20 digits, ITU-T E.118)"),
        startDate: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/, "startDate must be YYYY-MM-DD")
          .optional()
          .describe("Start of the period, YYYY-MM-DD inclusive (default: 7 days ago)"),
        endDate: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/, "endDate must be YYYY-MM-DD")
          .optional()
          .describe("End of the period, YYYY-MM-DD inclusive, max 7 days from start (default: today)"),
        limit: z
          .number()
          .int()
          .min(1)
          .max(50)
          .default(20)
          .optional()
          .describe("Maximum number of country_events to return, newest first (default 20, max 50)"),
      },
      annotations: { readOnlyHint: true, idempotentHint: true },
    },
    wrapHandler(
      "subscriber_country_history",
      "getSingleSubscriber",
      "read",
      ctx,
      async (
        {
          subscriberId,
          startDate,
          endDate,
          limit,
        }: { subscriberId: string; startDate?: string; endDate?: string; limit?: number },
        token: string,
      ) => {
        const effectiveLimit = limit ?? 20;
        const now = new Date();
        const end = endDate ?? isoDate(now);
        const start = startDate ?? isoDate(now, 7);

        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        const subscriberPayload = await client.call<unknown>("getSingleSubscriber", {
          iccid: subscriberId,
        });

        // Gy is the primary source. A failure here must not take the whole
        // tool down — the relay supplement below can still carry an answer.
        let gy = gyCountryHistoryFromResponse(undefined);
        let gyError: string | null = null;
        try {
          const events = await client.call<unknown>(
            "subscriberNetworkEventsOverPeriod",
            networkEventsOverPeriodParams(subscriberId, start, end),
          );
          gy = gyCountryHistoryFromResponse(events);
        } catch (err) {
          gyError = err instanceof Error ? err.message : String(err);
        }

        // Relay LU events, when relay happens to be on for this reseller. No
        // longer load-bearing: an empty buffer costs nothing now.
        let syntheticRingIccid: string | null = null;
        const msisdnRaw = msisdnFieldFromSubscriberPayload(subscriberPayload);
        if (msisdnRaw) {
          try {
            const k = ocsEventRingBufferIccidFromDigitsReference(msisdnRaw);
            if (k !== subscriberId) syntheticRingIccid = k;
          } catch {
            syntheticRingIccid = null;
          }
        }

        const primary = await listRecentOcsEvents(
          subscriberId,
          effectiveLimit,
          ["country.entered"],
          undefined,
          ctx.env.OCS_EVENT_ROUTING,
        );
        let relayEvents = primary.events;
        if (syntheticRingIccid) {
          const secondary = await listRecentOcsEvents(
            syntheticRingIccid,
            effectiveLimit,
            ["country.entered"],
            undefined,
            ctx.env.OCS_EVENT_ROUTING,
          );
          relayEvents = mergeOcsEventOutputsNewestFirst(
            [primary.events, secondary.events],
            effectiveLimit,
          );
        }

        const countryEvents = [
          ...relayEvents.map((e) => ({
            event_id: e.event_id,
            new_country: (e.data["new_country"] as string | undefined) ?? null,
            previous_country: (e.data["previous_country"] as string | undefined) ?? null,
            mcc: e.data["mcc"] ?? null,
            mnc: e.data["mnc"] ?? null,
            timestamp: e.timestamp,
            source: e.data["source"] ?? "relay-lu",
          })),
          ...staysAsCountryEvents(gy.stays),
        ].slice(0, effectiveLimit);

        return {
          content: [
            {
              type: "text" as const,
              text: JSON.stringify(
                {
                  subscriber_id: subscriberId,
                  period: { start, end },
                  stays: gy.stays,
                  transitions: gy.transitions,
                  gy_events_considered: gy.events_considered,
                  gy_events_skipped: gy.events_skipped,
                  gy_teardown_artifacts_dropped: gy.teardown_artifacts_dropped,
                  gy_error: gyError ?? undefined,
                  country_events: countryEvents,
                  total_country_events: countryEvents.length,
                  total_in_buffer: primary.total_in_buffer,
                  note:
                    countryEvents.length === 0
                      ? `No Gy charging records with a country between ${start} and ${end}, and no relay country events in the 24h ring-buffer. Widen the period, or check the subscriber has attached at all.`
                      : undefined,
                },
                null,
                2,
              ),
            },
          ],
        };
      },
    ),
  );
}
